// Pure helpers for the V3 settings, account and help views (M25, M28, M98–M100, M111, M113, M115, M116); no network, no storage.
import { own, text, lookup, validIsoDate } from "./memberDerive";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const isRec = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const str = (v) => (typeof v === "string" ? v.trim() : "");
const idOf = (v) => (typeof v === "number" && Number.isSafeInteger(v) ? String(v) : typeof v === "string" && v.trim() && !/[\u0000-\u001f\u007f/\\]/.test(v) ? v : "");
const iso = (v) => (validIsoDate(v) ? v : null);
const cpLen = (v) => Array.from(str(v)).length;

export function dateDe(v) {
  if (!iso(v)) return "";
  const p = Object.fromEntries(new Intl.DateTimeFormat("de-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric" }).formatToParts(new Date(v)).map((x) => [x.type, x.value]));
  return `${p.day}.${p.month}.${p.year}`;
}

// M25: summary of the display choices that are switched on; colour or contrast alone never decides.
const DISPLAY = [["large_text", "Grosse Schrift"], ["high_contrast", "Hoher Kontrast"], ["reduce_motion", "Weniger Bewegung"]];
export const displaySummary = (a11y) => DISPLAY.filter(([k]) => isRec(a11y) && a11y[k] === true).map(([, l]) => l).join(" · ") || "Standard · wie auf deinem Gerät";
export const privacyChanged = (draft, saved) => draft.discoverable !== saved.discoverable || draft.allow_contact_requests !== saved.allow_contact_requests;

// M100: "Bestätigt" only with an explicit flag from the Core (D-96); otherwise the address is just the sign-in address.
export function emailText(profile) {
  const mail = str(own(profile, "email"));
  if (!mail) return "Nicht angegeben";
  return own(profile, "email_verified") === true ? `${mail} · Bestätigt` : `${mail} · Anmeldeadresse`;
}

// GET /member/sessions (proposed, D-97); a broken shape is null.
export function normalizeSessions(d) {
  const list = own(d, "sessions");
  if (!Array.isArray(list)) return null;
  return list.filter(isRec).map((s) => ({ id: idOf(own(s, "id")), device: str(own(s, "device_label")) || "Unbekanntes Gerät", lastSeen: iso(own(s, "last_seen_at")), current: own(s, "current") === true }))
    .filter((s) => s.id);
}
export function otherSessionsText(list) {
  if (!Array.isArray(list)) return "Einzeln prüfen und beenden · Noch nicht verfügbar";
  const n = list.filter((s) => !s.current).length;
  return n === 0 ? "Keine weiteren Sitzungen" : n === 1 ? "1 weitere Sitzung" : `${n} weitere Sitzungen`;
}
export const SECURITY_CHOICES = ["email", "password", "current", "others"];

// M98: scope, period and the separately chosen private Access Pass (D-93).
export const EXPORT_SCOPES = [["profile_content", "Eigene Profilangaben und Inhalte"], ["profile", "Nur Profilangaben"], ["settings", "Nur Einstellungen"]];
export const PASS_CHOICES = [["no", "Nicht ausgewählt"], ["yes", "Mit auswählen"]];
export const scopeText = (k) => (EXPORT_SCOPES.find(([s]) => s === k) || [, "Umfang nicht angegeben"])[1];
export function periodText(k) {
  if (k === "all") return "Gesamter Zeitraum";
  const m = typeof k === "string" ? /^(\d{4})-(0[1-9]|1[0-2])$/.exec(k) : null;
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "Zeitraum nicht angegeben";
}
// The current month first (as in the template), the eleven before it and the whole period.
export function exportPeriods(now = Date.now(), timeZone = "Europe/Zurich") {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit" }).formatToParts(new Date(now)).map((x) => [x.type, x.value]));
  let y = Number(p.year); let m = Number(p.month);
  const out = [];
  for (let i = 0; i < 12; i += 1) {
    const k = `${y}-${String(m).padStart(2, "0")}`;
    out.push([k, periodText(k)]);
    m -= 1; if (m === 0) { m = 12; y -= 1; }
  }
  return [...out, ["all", "Gesamter Zeitraum"]];
}
export function exportErrors({ scope, period, pass, confirmed }, periods) {
  const e = {};
  if (!EXPORT_SCOPES.some(([k]) => k === scope)) e.scope = "Bitte wähle einen Umfang.";
  if (!periods.some(([k]) => k === period)) e.period = "Bitte wähle einen Zeitraum.";
  if (!PASS_CHOICES.some(([k]) => k === pass)) e.pass = "Bitte entscheide, ob der Access Pass dazugehört.";
  if (confirmed !== true) e.confirmed = "Bitte bestätige, dass du den Datenumfang geprüft hast.";
  return e;
}
export const exportPayload = ({ scope, period, pass }) => ({ scope, period, include_access_pass: pass === "yes" });

// GET /member/account/exports and /member/account/export/{id} (proposed, D-93/D-94).
const EXPORT_STATE = { angefordert: "requested", in_arbeit: "processing", bereit: "ready", abgelaufen: "expired" };
export const EXPORT_STATE_TEXT = { requested: "Angefordert", processing: "Wird zusammengestellt", ready: "Bereit", expired: "Abgelaufen", unknown: "Status unbekannt" };
function exportOf(e) {
  if (!isRec(e)) return null;
  const v = { id: idOf(own(e, "id")), scope: str(own(e, "scope")), period: str(own(e, "period")), state: lookup(EXPORT_STATE, own(e, "status")) || "unknown",
    readyAt: iso(own(e, "ready_at")), expiresAt: iso(own(e, "expires_at")) };
  return v.id ? v : null;
}
export const normalizeExport = (d) => exportOf(own(d, "export"));
export function normalizeExports(d) {
  const list = own(d, "exports");
  return Array.isArray(list) ? list.map(exportOf).filter(Boolean) : null;
}
export const exportLine = (x) => [periodText(x.period), EXPORT_STATE_TEXT[x.state]].join(" · ");

// M99: GET /member/account/deletion-impact (proposed, D-95) and active grants from GET /member/access-pass/requests (Core 1.1).
export function normalizeImpact(d) {
  const i = own(d, "impact");
  if (!isRec(i)) return null;
  const list = own(i, "circle_responsibilities");
  if (!Array.isArray(list)) return null;
  return { circles: list.map((c) => str(text(own(own(c, "group"), "name")))).filter(Boolean) };
}
export function circleText(impact) {
  if (impact === undefined) return "Wird geladen …";
  if (impact === false) return "Nicht abrufbar";
  if (impact === null) return "Noch nicht verfügbar";
  return impact.circles.length ? `Vor der Anfrage klären: ${impact.circles.join(", ")}` : "Keine Kreisverantwortung";
}
export function grantsText(grants) {
  if (grants === undefined) return "Wird geladen …";
  if (!Array.isArray(grants)) return "Nicht abrufbar";
  const n = grants.filter((g) => g.state === "active").length;
  return n === 0 ? "Keine aktiven Freigaben" : `${n === 1 ? "1 aktive Freigabe" : `${n} aktive Freigaben`} · Vorher prüfen und gegebenenfalls beenden`;
}

// M113: own reports with their decision (GET /member/reports/mine, Core 1.1) and decisions about the own account (proposed, D-92).
const REPORT_STATE = { offen: "open", in_bearbeitung: "in_progress", "gelöst": "resolved", abgewiesen: "rejected" };
export const REPORT_STATE_TEXT = { open: "Offen", in_progress: "In Bearbeitung", resolved: "Gelöst", rejected: "Abgewiesen", unknown: "Status unbekannt" };
export function normalizeReportsMine(d) {
  const list = own(d, "reports");
  if (!Array.isArray(list)) return null;
  return list.filter(isRec).map((r) => ({ id: idOf(own(r, "id") ?? own(r, "report_id")), state: lookup(REPORT_STATE, own(r, "status")) || "unknown",
    feedback: str(text(own(r, "feedback"))), feedbackAt: iso(own(r, "feedback_at")), createdAt: iso(own(r, "created_at")) })).filter((r) => r.id);
}
export const isDecided = (r) => r.state === "resolved" || r.state === "rejected";
export const reportLine = (r) => [REPORT_STATE_TEXT[r.state], r.feedback].filter(Boolean).join(" · ");

const DECISION_KIND = { write_restriction: "Vorübergehende Schreibbeschränkung", post_hidden: "Beitrag ausgeblendet", post_removed: "Beitrag entfernt", comment_hidden: "Kommentar ausgeblendet", comment_removed: "Kommentar entfernt" };
export function normalizeDecisions(d) {
  const list = own(d, "decisions");
  if (!Array.isArray(list)) return null;
  return list.filter(isRec).map((x) => ({ id: idOf(own(x, "id")), kind: lookup(DECISION_KIND, own(x, "kind")) || "", decidedAt: iso(own(x, "decided_at")), until: iso(own(x, "until")), appealable: own(x, "appealable") === true }))
    .filter((x) => x.id && x.kind);
}

// One choice list: own-account decisions that can be appealed, then decided own reports.
export function decisionOptions(decisions, reports) {
  const mine = (decisions || []).filter((x) => x.appealable).map((x) => ({ id: `d:${x.id}`, label: [x.kind, x.until ? `bis ${dateDe(x.until)}` : dateDe(x.decidedAt)].filter(Boolean).join(" · ") }));
  const rep = (reports || []).filter(isDecided).map((r) => ({ id: `r:${r.id}`, label: `Entscheidung zu deiner Meldung${r.createdAt ? ` vom ${dateDe(r.createdAt)}` : ""} · ${REPORT_STATE_TEXT[r.state]}` }));
  return [...mine, ...rep];
}

export const APPEAL_MAX = 1000;
export function appealErrors({ decision, body }, options) {
  const e = {};
  if (!options.some((o) => o.id === decision)) e.decision = "Bitte wähle die Entscheidung, zu der du eine Prüfung möchtest.";
  if (!cpLen(body)) e.body = "Bitte beschreibe deinen Einwand.";
  else if (cpLen(body) > APPEAL_MAX) e.body = `Der Text darf höchstens ${APPEAL_MAX} Zeichen lang sein.`;
  return e;
}
